import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import type { Market } from "../api/types";
import { useNow } from "../hooks/useNow";
import {
  countdownTone,
  findLiveWindow,
  findNeighbours,
  formatCountdown,
  formatWindow,
  parseSeriesSlug,
  remainingInWindow,
  windowPhase,
  type CountdownTone,
  type SeriesNeighbour,
  type SeriesParts,
} from "../lib/series";
import { Field, Val, W } from "./Field";

/**
 * Навигация по окнам рекуррентной серии.
 *
 * Существует потому, что пятиминутный рынок заканчивается каждые пять минут, а
 * попасть на следующий можно было только через дашборд. Оператор, у которого
 * окно закрылось, тратил на это дорогие секунды — и попадал на страницу списка,
 * где ещё надо найти нужную строку.
 *
 * Листовой компонент: держит `useNow(1000)` ради обратного отсчёта, и правило
 * hooks/useNow.ts требует, чтобы такой тик не сидел в компоненте, который тянет
 * за собой перерисовку половины экрана.
 */
interface Props {
  market: Market;
  markets: readonly Market[];
  /** Чем подписать рынок вне серии. Без него такой рынок не рисуется вовсе. */
  fallback?: string | undefined;
}

/** Разобранное состояние серии — общее для шапки и для плашки. */
function useSeries(market: Market, markets: readonly Market[]) {
  const nowMs = useNow(1000);
  const now = new Date(nowMs);
  const parts = parseSeriesSlug(market.slug);
  if (!parts) return null;

  const phase = windowPhase(parts, now);
  return {
    now,
    parts,
    phase,
    remaining: remainingInWindow(parts, now),
    ...findNeighbours(markets, parts),
    live: phase === "past" ? findLiveWindow(markets, parts, now) : null,
  };
}

/**
 * Окно серии и остаток — НАЧАЛО шапки.
 *
 * Фрагмент, а не блок с собственной строкой: шапка рынка однострочная, и всё,
 * что в ней есть, обязано вставать в общий перенос по словам, а не занимать
 * свой ряд.
 *
 * Ссылки на соседние окна уехали отсюда в конец строки (`SeriesLinks`) и это не
 * перестановка ради симметрии: между «что я торгую» и «на чём решение» они
 * стояли клином, а нужны раз в пять минут — когда окно кончилось.
 *
 * `fallback` показывается для рынков вне серии — у выборов и макрособытий нет
 * ни окна, ни соседей, и заменять их название пустыми стрелками нельзя.
 */
export function SeriesNav({ market, markets, fallback }: Props) {
  const s = useSeries(market, markets);
  if (!s) {
    return fallback ? <span className="font-medium">{fallback}</span> : null;
  }

  return (
    <>
      <Field w={W.window}>
        {/* Одной строкой, а не тремя вставками: разряды окна разделены точками
            и обязаны стоять на расстоянии слова. Разбей их на элементы ряда, и
            зазор поля растащил бы «BTC · 5m · 17:25–17:30» на три величины. */}
        <span className="num font-medium">
          {`${s.parts.asset} · ${s.parts.duration} · ${formatWindow(s.parts, s.now)}`}
        </span>
      </Field>
      {/* Фаза окна — один слот на три состояния: отсчёт сменяется надписью
          «окно закончилось», и без запаса ширины на этой смене ехала бы вся
          шапка. */}
      {s.phase === "live" && (
        <Field
          label="осталось"
          w={W.phase}
          title={
            "до конца окна. Жёлтый — итоговое окно усреднения уже началось, и " +
            "каждая новая секунда попадает в итог. Красный — исход вот-вот " +
            "перестанет зависеть от рынка, заявка может уже не успеть"
          }
        >
          <Val color={countdownColor(countdownTone(s.remaining))}>
            {formatCountdown(s.remaining)}
          </Val>
        </Field>
      )}
      {s.phase === "upcoming" && (
        <Field w={W.phase}>
          <span className="text-[var(--color-muted)]">ещё не открыто</span>
        </Field>
      )}
      {s.phase === "past" && (
        <Field w={W.phase}>
          <span className="num" style={{ color: countdownColor("over") }}>
            окно закончилось
          </span>
        </Field>
      )}
    </>
  );
}

/** Ссылки на соседние окна — КОНЕЦ шапки, рядом с закрытием рынка. */
export function SeriesLinks({ market, markets }: Props) {
  const s = useSeries(market, markets);
  if (!s) return null;
  return (
    <span className="flex items-center gap-2">
      <NeighbourLink neighbour={s.prev} now={s.now} direction="prev" />
      <NeighbourLink neighbour={s.next} now={s.now} direction="next" />
    </span>
  );
}

/** Токены темы, а не свои значения: палитра терминала одна. */
function countdownColor(tone: CountdownTone): string {
  switch (tone) {
    case "urgent":
      return "var(--color-sell)";
    case "soon":
      return "var(--color-warn)";
    case "over":
      return "var(--color-muted)";
    default:
      return "var(--color-buy)";
  }
}

/**
 * Приглашение уйти на идущее окно. Рисуется, только когда текущее закончилось.
 *
 * Автоматического перехода нет намеренно: оператор в эту минуту разбирает
 * исполнение по закончившемуся окну, и увести у него экран из-под рук значит
 * отнять единственный момент, когда он смотрит на результат сделки.
 */
export function WindowExpired({ market, markets }: Props) {
  const s = useSeries(market, markets);
  if (!s || s.phase !== "past") return null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
      <span>
        Окно {formatWindow(s.parts, s.now)} закончилось — торговать по этому рынку уже нечем.
      </span>
      {s.live ? (
        <Link
          to={`/market/${encodeURIComponent(s.live.conditionId)}`}
          className="rounded border border-amber-400 px-2 py-1 font-medium hover:bg-amber-500/20"
        >
          Перейти к идущему окну {formatWindow(s.live.parts, s.now)} →
        </Link>
      ) : (
        // Не выдумываем ближайшее будущее окно: на него нельзя поставить, оно
        // ещё не открылось. Справочник обновляется раз в полминуты, и честнее
        // сказать, чего мы ждём.
        //
        // С тех пор как трейдер кладёт в справочник и следующее окно (а не
        // только идущее), сюда попадают лишь настоящие отказы: Gamma не
        // ответила, серия кончилась. Ждать в них полминуты незачем — кнопка
        // спрашивает справочник немедленно.
        <span className="flex items-center gap-2 text-[var(--color-muted)]">
          идущего окна ещё нет в справочнике
          <RefreshMarkets />
        </span>
      )}
    </div>
  );
}

/**
 * Спросить справочник немедленно, не дожидаясь очередного опроса.
 *
 * Ключ тот же, что у дашборда и у страницы рынка, — обновится всё сразу.
 * Учащать сам опрос ради этого нельзя: справочник отдаёт до пятисот строк с
 * токенами, а прод живёт на одном ядре.
 */
function RefreshMarkets() {
  const queryClient = useQueryClient();
  return (
    <button
      type="button"
      onClick={() => void queryClient.invalidateQueries({ queryKey: ["markets"] })}
      className="rounded border border-amber-500/40 px-1.5 py-0.5 hover:bg-amber-500/20"
      title="перечитать справочник рынков прямо сейчас"
    >
      обновить
    </button>
  );
}

function NeighbourLink({
  neighbour,
  now,
  direction,
}: {
  neighbour: SeriesNeighbour | null;
  now: Date;
  direction: "prev" | "next";
}) {
  const arrow = direction === "prev" ? "←" : "→";
  const label = direction === "prev" ? "пред. окно" : "след. окно";

  // Отсутствующий сосед остаётся приглушённым текстом, а не исчезает: элемент,
  // пропадающий каждые пять минут, двигает всю шапку под курсором.
  if (!neighbour) {
    return (
      <span className="text-[var(--color-muted)] opacity-40">
        {direction === "prev" ? `${arrow} ${label}` : `${label} ${arrow}`}
      </span>
    );
  }

  const window = formatWindow(neighbour.parts, now);
  const text = direction === "prev" ? `${arrow} ${window}` : `${window} ${arrow}`;
  return (
    <Link
      to={`/market/${encodeURIComponent(neighbour.conditionId)}`}
      title={
        neighbour.tracked
          ? `${label}: ${window}`
          : `${label}: ${window} — трейдер на него не подписан, стакана не будет`
      }
      className="num text-[var(--color-muted)] hover:text-[var(--color-accent)] hover:underline"
    >
      {text}
      {/* Неотслеживаемый сосед существует, и прятать ссылку нельзя. Но и молчать
          нельзя: оператор попадёт на экран без стакана и решит, что сломалось. */}
      {!neighbour.tracked && <span className="ml-0.5 text-amber-300">~</span>}
    </Link>
  );
}

export type { SeriesParts };
